// API クライアント
// Worker の /api/* に対して型付きリクエストを投げる

import type { WorkDetail } from '../components/reportUtils'

const BASE = import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    credentials: 'include',
    ...init,
  })

  if (res.status === 401) {
    if (!window.location.pathname.startsWith('/login')) {
      window.location.replace('/login')
    }
    throw new Error('セッションが切れました。再ログインしてください。')
  }

  if (res.status === 403) {
    const err = await res.json().catch(() => ({ error: res.statusText, code: '' }))
    if ((err as any).code === 'MUST_CHANGE_PASSWORD') {
      if (!window.location.pathname.startsWith('/account')) {
        window.location.replace('/account?force_change_password=1')
      }
      throw new Error('初期パスワードの変更が必要です')
    }
    // 追加認証（パスキー/メール）の初期設定が済むまで保護対象APIは使えない
    if ((err as any).code === 'MFA_SETUP_REQUIRED') {
      if (!window.location.pathname.startsWith('/account')) {
        window.location.replace('/account?mfa_setup=1')
      }
      throw new Error('追加認証の設定が必要です')
    }
    throw new Error((err as any).error ?? 'Forbidden')
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as any).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

// ヘルプスタッフ公開提出画面（/help-submit/:token）専用の軽量リクエスト関数。
// 未ログイン前提のため、request() の 401→/login リダイレクト処理を通さない。
// 404 はトークンが存在しない/期限切れ/無効化済みのいずれかを意味する。
async function helpSubmitRequest<T>(token: string, init?: RequestInit, path = ''): Promise<T> {
  const res = await fetch(`${BASE}/help-submit/${encodeURIComponent(token)}${path}`, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as any).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

// 画像アップロードのみ multipart/form-data のため専用の軽量fetch
async function helpSubmitUploadImage(token: string, file: File): Promise<{ success: boolean; url: string; key: string }> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`${BASE}/help-submit/${encodeURIComponent(token)}/images`, {
    method: 'POST',
    body: form,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as any).error ?? 'アップロードに失敗しました')
  }
  return res.json()
}

// --------------------------------------------------------
// 型定義
// --------------------------------------------------------

export type Work = {
  id: string
  title: string
  frequency: 'weekly' | 'monthly' | 'bimonthly' | 'irregular'
  rate_per_page: number
  pages_per_ep: number
  has_original_story: number
  publisher: string | null
  editor_name: string | null
  status: 'active' | 'hiatus' | 'completed'
  notes: string | null
  archived_at: string | null
  display_order: number
  episode_count?: number
  active_episodes?: number
}

export type Episode = {
  id: string
  work_id: string
  episode_number: number
  title: string | null
  page_count: number
  deadline_name_check: string | null
  deadline_submit: string | null
  deadline_entry: string | null
  published_at: string | null
  status: 'planned' | 'in_progress' | 'submitted' | 'completed' | 'hiatus'
  notes: string | null
  work_type: 'story' | 'tankobon' | 'color'
  work_label: string | null
  work_title?: string
  frequency?: string
  estimated_fee?: number
  stage_summary?: string
  /** "stage:progress_pages:target_pages,..." 形式（進捗ありのstageのみ） */
  stage_progress_summary?: string
  stages_done?: number
}

export type Stage = {
  id: string
  episode_id: string
  stage: string
  status: 'not_started' | 'in_progress' | 'done'
  started_at: string | null
  completed_at: string | null
  // Phase 2.5: 工程別ページ進捗
  progress_pages?: number
  target_pages?: number | null
  progress_note?: string | null
  progress_updated_at?: string | null
}

// Phase 5-C: アシスタントスキル設定
export type AssistantStageCapability = {
  id?: string
  assistant_id?: string
  stage_key: string
  skill_level: number
  preference_order: number
  capacity_multiplier: number
  is_active: number
  note?: string | null
}

export type Assistant = {
  id: string
  name: string
  role_type: 'background' | 'finishing' | 'ai_art' | 'research' | 'general'
  pc_type?: string | null
  daily_rate: number
  overtime_hourly: number
  transport_cap: number
  meal_included: number
  meal_rate: number
  is_active: number
  joined_at: string | null
  notes?: string | null
  email?: string | null
  payroll_enabled?: number
  total_days_this_month?: number
  total_pay_this_month?: number
  // 退職管理 (migration 0039)
  employment_status?: 'active' | 'leaving' | 'retired'
  retired_at?: string | null
  retirement_note?: string | null
  login_enabled?: number
  shift_enabled?: number
  report_enabled?: number
  final_settled_at?: string | null
  // オンボーディング (migration 0040)
  display_name?: string | null
  phone?: string | null
  contact_note?: string | null
  onboarding_status?: 'active' | 'draft' | string
  account_setup_status?: 'pending' | 'created' | 'logged_in' | string
  initial_password_changed?: number
  training_enabled?: number
  training_days_completed?: number
  training_days_required?: number
  training_daily_rate?: number
  onboarding_note?: string | null
  // Phase 5-C
  stage_capabilities?: AssistantStageCapability[]
}

export type Attendance = {
  id: string
  assistant_id: string
  work_date: string
  check_in: string
  check_out: string
  overtime_min: number
  transport_fee: number
  meal_provided: number
  work_type?: 'full_time' | 'part_time'
  work_minutes?: number
  break_minutes?: number
  daily_total: number | null
  assistant_name?: string
}

export type Expense = {
  id: string
  category: string
  description: string
  amount: number
  is_fixed: number
  payment_day: number | null
  payment_method: string
  due_at: string | null
  paid_at: string | null
  is_paid: number
  related_month: string | null
  notes: string | null
  // ★ 追加: 口座・区分
  payment_account_key: string       // 'business' | 'personal_manga'
  cashflow_scope: string            // 'business' | 'tax' | 'personal_non_business'
}

export type Revenue = {
  id: string
  work_id: string | null
  episode_id: string | null
  revenue_type: string
  amount: number
  tax_reserve_rate: number
  tax_reserve_amount: number | null
  expected_at: string | null
  received_at: string | null
  description: string | null
  work_title?: string
}

export type DailyReport = {
  id: string
  assistant_id: string | null
  report_date: string
  episode_id: string | null
  stage: string | null
  pages_done: number
  description: string | null
  issues: string | null
  image_urls: string | string[]
  assistant_name?: string
  work_title?: string
  // ヘルプスタッフの提出済み日報がマージされた行かどうか（読み取り専用・編集/削除不可）
  is_help?: boolean
  source_type?: 'daily_report' | 'help'
  // work_details（JSON配列。daily_reportsと同一フォーマット）があれば表示側はこちらを最優先で使う
  work_details?: string | WorkDetail[] | null
  handover_notes?: string | null
}

// ヘルプスタッフ（単発勤務者）
export type HelpStaff = {
  id: string
  name: string
  default_unit_rate: number
  default_transport_fee: number
  default_overtime_rate: number
  notes: string | null
  is_active: number
  created_at: string
  updated_at: string
}

export type HelpWorkEntry = {
  id: string
  help_staff_id: string
  help_staff_name?: string
  work_date: string
  payment_target_month: string
  unit_rate: number
  transport_fee: number
  reported_transport_fee: number | null
  notes: string | null
  status: 'pending' | 'draft' | 'submitted' | 'confirmed' | 'cancelled'
  report_description: string | null
  report_issues: string | null
  submitted_at: string | null
  confirmed_at: string | null
  paid_at: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  // 勤務情報
  start_time: string | null
  end_time: string | null
  break_minutes: number
  work_note: string | null
  // 日報（JSON配列。daily_reports.work_detailsと同一フォーマット）
  work_details: string | null
  // 作業画像（JSON配列。daily_reports.image_urlsと同一フォーマット）
  image_urls: string | null
  // 一時保存日時
  draft_saved_at: string | null
  // 残業: 申告値と確定値・時給
  reported_overtime_minutes: number
  overtime_minutes: number
  overtime_rate: number
  // その他経費: 申告値と確定値
  reported_other_expense: number | null
  other_expense: number
  expense_note: string | null
  // ライフサイクル（無効化・復帰）
  cancelled_at: string | null
  cancelled_reason: string | null
  restored_at: string | null
  status_before_cancel: string | null
}

// ヘルプスタッフ年間支払集計（GET /api/help-work-entries/annual-summary）
export type HelpAnnualSummary = {
  year: string
  status_filter: 'paid' | 'confirmed'
  staff: {
    help_staff_id: string
    name: string
    monthly: Record<string, number>
    total: number
    entries_count: number
  }[]
}

// 統合月次一覧（GET /api/monthly-payees）の1行
export type MonthlyPayee = {
  source_type: 'assistant' | 'help' | 'bonus'
  source_id: string
  name: string
  amount: number
  status: string
  is_settled: boolean
  details: Record<string, unknown>
}

// help-work-entries の作成/更新リクエストボディ用（work_details は送信時 WorkDetail[]、
// 読み取り時の HelpWorkEntry.work_details は保存済みJSON文字列のため型が異なる）
export type HelpWorkEntryWriteFields = {
  help_staff_id: string
  work_date: string
  payment_target_month: string
  unit_rate: number
  transport_fee: number
  notes: string
  report_issues: string
  start_time: string
  end_time: string
  break_minutes: number
  work_note: string
  work_details: WorkDetail[]
  overtime_minutes: number
  overtime_rate: number
  other_expense: number
  expense_note: string
}

// 公開提出画面（/help-submit/:token）が受け取る最小限の情報。金額情報は含まない。
export type HelpSubmitData = {
  help_staff_name: string
  work_date: string
  status: 'pending' | 'draft' | 'submitted' | 'confirmed'
  // 勤務情報
  start_time: string | null
  end_time: string | null
  break_minutes: number
  reported_overtime_minutes: number
  work_note: string | null
  // 日報
  work_details: WorkDetail[]
  report_issues: string | null
  // 経費
  reported_transport_fee: number | null
  reported_other_expense: number | null
  expense_note: string | null
  // 画像
  image_urls: string[]
  // 選択肢
  works: { id: string; title: string }[]
  // 状態
  draft_saved_at: string | null
  submitted: boolean
  confirmed: boolean
  paid: boolean
}

// PUT /api/help-submit/:token, PUT /api/help-submit/:token/draft 共通のボディ型
// reported_overtime_minutes は含まない（サーバー側が必ず再計算するため送信しない）
export type HelpSubmitUpdateBody = {
  start_time?: string
  end_time?: string
  break_minutes?: number
  work_note?: string
  work_details?: WorkDetail[]
  report_issues?: string
  reported_transport_fee?: number
  reported_other_expense?: number
  expense_note?: string
}

// ★ 追加: 口座残高サマリー型
export type BankAccountSummary = {
  account_key: string
  name: string
  current_balance: number
  upcoming_withdrawals_total: number
  projected_balance: number
  alert_enabled?: number
  deduction_breakdown?: {
    expenses_total: number
    confirmed_salary_total?: number
    confirmed_help_salary_total?: number
    total: number
  }
}

// ★ 追加: 口座残高の固定費・支払・給与内訳型
export type FixedCostBreakdownItem = {
  id: string
  due_at: string | null
  name: string
  amount: number
  status: 'unpaid' | 'overdue'
  target_month: string | null
  account_key: string
}
export type PaymentBreakdownItem = {
  id: string
  due_at: string | null
  name: string
  amount: number
  status: 'unpaid' | 'overdue'
  category: string
  target_month: string | null
  account_key: string
}
export type PayrollBreakdownRow = {
  target_month: string
  status: string
  count: number
  total_amount: number
}
export type AccountBreakdown = {
  fixed_costs: FixedCostBreakdownItem[]
  payments: PaymentBreakdownItem[]
  assistant_payroll: PayrollBreakdownRow[]
  help_payroll: PayrollBreakdownRow[]
}

// ★ 追加: 残高不足アラート型
export type WithdrawalAlert = {
  account_key: string
  account_name: string
  expense_id: string
  description: string
  amount: number
  due_at: string
  days_until_due: number
  projected_balance_after: number
}

export type DashboardData = {
  today: string
  upcoming_deadlines: (Episode & {
    work_title: string; stage_summary?: string; days_until_submit: number | null
  })[]
  unpaid_expenses: (Expense & { days_until_due: number | null })[]
  today_attendance: (Attendance & { name: string; role_type: string; is_recorded: number })[]
  pending_revenues: (Revenue & { days_until: number | null })[]
  recent_reports: (DailyReport & { assistant_name: string; issues?: string })[]
  monthly_labor: {
    total: number; working_days: number; man_days: number; meal_total: number
    help_total?: number; combined_total?: number
  } | null
  monthly_revenue: { received: number; tax_reserved: number; pending_income: number } | null
  spendable: number
  labor_chart: { month: string; total: number; man_days: number }[]
  shift_needs_review_count: number
  // ★ 追加
  bank_accounts?: BankAccountSummary[]
  withdrawal_alerts?: WithdrawalAlert[]
  account_breakdown?: AccountBreakdown
  // Phase 4-B
  production_plan_today?: ProductionPlanTodayItem[]
  production_plan_overdue?: ProductionPlanOverdueItem[]
  production_plan_upcoming?: ProductionPlanOverdueItem[]
}

// ★ 追加: 制作進行計画型
export type CapacitySetting = {
  id: string
  stage_key: string
  worker_type: 'artist' | 'assistant'
  unit: 'pages' | 'days'
  normal_capacity: number
  rush_capacity: number
  can_work_weekends: number
  can_parallel: number
  sort_order: number
  is_active: number
  created_at: string
  updated_at: string
}

export type SingleTaskRule = {
  id: string
  work_type: string
  label: string
  default_days: number
  blocks_artist_day: number
  has_page_quota: number
  note: string | null
  sort_order: number
  is_active: number
  created_at: string
  updated_at: string
}

// Windows/Mac 同時作業可能人数（制作環境設定）
export type ResourceSettings = {
  id: number
  windows_capacity: number
  mac_capacity: number
  is_enabled: number
  notes: string | null
  updated_at: string
}

export type TodayPlanRecommendation = {
  priority: number
  risk: 'critical' | 'danger' | 'warning' | 'safe'
  risk_label: string
  work_id: string
  episode_id: string
  work_title: string
  episode_number: number | null
  episode_label: string
  work_type: string
  stage_key: string | null
  stage_label: string
  suggested_amount: number
  unit: string
  deadline: string
  days_until_deadline: number
  required_days: number
  available_days: number
  slack_days: number
  reason: string
}

export type TodayPlan = {
  date: string
  summary: {
    total_items: number
    critical_count: number
    warning_count: number
  }
  recommendations: TodayPlanRecommendation[]
}

// ★ 追加: 日報→工程進捗 反映候補型
export type ReportProgressCandidate = {
  // source_type='help' の場合 daily_report_id は null、help_work_entry_id/help_staff_id が入る
  source_type?:              'assistant' | 'help'
  daily_report_id:           string | null
  help_work_entry_id?:       string | null
  help_staff_id?:            string | null
  detail_index:              number
  report_date:               string
  assistant_name:            string
  work_id:                   string
  work_title:                string
  episode_id:                string
  episode_number:            number
  stage_key:                 string
  stage_label:               string
  pages_delta:               number
  current_progress_pages:    number
  target_pages:              number
  projected_progress_pages:  number
  reflected:                 boolean
}

// ★ 追加: 日報→工程進捗 反映履歴型
export type ReportProgressReflectionHistory = {
  id:                  string
  source_type?:        'assistant' | 'help'
  daily_report_id:     string | null
  help_work_entry_id?: string | null
  help_staff_id?:      string | null
  detail_index:    number
  report_date:     string
  work_id:         string
  work_title:      string
  episode_id:      string
  episode_number:  number | null
  stage_key:       string
  stage_label:     string
  pages_delta:     number
  progress_before: number
  progress_after:  number
  target_pages:    number | null
  status_before:   string
  status_after:    string
  applied_by:      string | null
  applied_by_name: string
  applied_at:      string
  assistant_id:    string | null
  assistant_name:  string
  undone_at:       string | null
  undone_by:       string | null
  undone_by_name:  string
  undo_reason:     string | null
  note:            string | null
  is_undone:       boolean
}

// ★ 追加: 残高チェック結果型（withdrawal-check API 用）
export type WithdrawalCheckResult = {
  accounts: (BankAccountSummary & { upcoming_withdrawals_total: number })[]
  insufficient_items: WithdrawalAlert[]
}

// ★ Phase 4-B/4-C: 保存済み制作予定型
export type ProductionPlanTodayItem = {
  id: string
  plan_date: string
  work_title: string | null
  episode_number: number | null
  stage_key: string | null
  stage_label: string | null
  planned_amount: number
  unit: string
  risk: string | null
  reason: string | null
  is_locked: number
  status: string
  manual_note: string | null
  // Phase 4-C
  worker_type?: string | null
  capacity_cost?: number
  moved_from_date?: string | null
}

export type ProductionPlanOverdueItem = {
  id: string
  plan_date: string
  work_title: string | null
  episode_number: number | null
  stage_label: string | null
  planned_amount: number
  unit: string
  status: string
  worker_type?: string | null
  moved_from_date?: string | null
}

// ★ Phase 4-C / 5-A / 5-B / 5-C: 日別キャパシティサマリー
export type ProductionPlanDaySummary = {
  date: string
  artist_used: number
  assistant_used: number
  // Phase 5-A: 確定シフトベースのアシスタントキャパシティ
  assistant_capacity?: number          // 確定アシスタント数（0 = 不在）
  assistant_count?: number             // アシスタント人数
  assistant_names?: string[]           // アシスタント名一覧
  assistant_capacity_source?: string   // 'confirmed_shifts' | 'none'
  // conflict_level 値: null | 'warning' | 'over_capacity'
  conflict_level: string | null
  conflict_note?: string | null        // 不在日警告メッセージなど
  // Phase 5-B: 役割別キャパシティ
  assistants_by_role?: Record<string, string[]>  // role → names
  role_capacity?: Record<string, number>          // role → count
  role_usage?: Record<string, number>             // role → used
  role_conflict_notes?: string[]                  // 役割不一致の警告
  // Phase 5-C: アシスタント個別使用状況
  assistant_usage?: {
    assistant_id: string
    name: string
    used_capacity: number
    remaining_capacity: number
    assigned_stages: string[]
  }[]
  capability_notes?: string[]         // 能力不足の警告
}

export type ProductionPlanToday = {
  date: string
  today_items: ProductionPlanTodayItem[]
  overdue_items: ProductionPlanOverdueItem[]
  upcoming_items: ProductionPlanOverdueItem[]
  summary: {
    today_count: number
    overdue_count: number
    upcoming_count: number
  }
}

// ★ Phase 4-A/4-C: 制作計画型
export type ProductionPlanItem = {
  id: string
  plan_run_id: string | null
  plan_date: string
  work_id: string | null
  episode_id: string | null
  work_type: string
  work_title: string | null
  episode_number: number | null
  stage_key: string | null
  stage_label: string | null
  planned_amount: number
  unit: string
  priority: number
  risk: string | null
  reason: string | null
  is_locked: number
  status: string
  manual_note: string | null
  // Phase 4-C
  worker_type?: string | null
  capacity_cost?: number
  is_full_day?: number
  moved_from_date?: string | null
  moved_reason?: string | null
  is_manual_moved?: number
  conflict_level?: string | null
  // Phase 5-D: 担当候補（表示のみ・DB保存なし）
  suggested_assistant_id?: string | null
  suggested_assistant_name?: string | null
  suggested_assistant_reason?: string | null
  suggested_assistant_confidence?: 'high' | 'medium' | 'low' | null
}

export type ProductionPlanPreviewItem = {
  work_id: string
  episode_id: string
  work_type: string
  work_title: string
  episode_number: number | null
  stage_key: string | null
  stage_label: string
  planned_amount: number
  unit: string
  priority: number
  risk: string
  reason: string
  // Phase 4-C
  worker_type?: string | null
  capacity_cost?: number
  is_full_day?: number
  // Phase 5-D: 担当候補（表示のみ・DB保存なし）
  suggested_assistant_id?: string | null
  suggested_assistant_name?: string | null
  suggested_assistant_reason?: string | null
  suggested_assistant_confidence?: 'high' | 'medium' | 'low' | null
}

export type ProductionPlanDay = {
  date: string
  items: ProductionPlanPreviewItem[]
}

// --------------------------------------------------------
// API 関数
// --------------------------------------------------------

export const api = {
  // ダッシュボード
  dashboard: {
    get: () => request<DashboardData>('/dashboard'),
  },

  // 作品
  works: {
    list:   (params?: { include_archived?: boolean }) =>
      request<Work[]>(`/works${params?.include_archived ? '?include_archived=1' : ''}`),
    get:    (id: string) => request<Work & { episodes: Episode[] }>(`/works/${id}`),
    create: (body: Partial<Work>) =>
      request('/works', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<Work>) =>
      request(`/works/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    archive: (id: string) => request(`/works/${id}/archive`, { method: 'POST' }),
    restore: (id: string) => request(`/works/${id}/restore`, { method: 'POST' }),
    delete:  (id: string) => request(`/works/${id}`, { method: 'DELETE' }),
  },

  // 話数
  episodes: {
    list: (params?: { work_id?: string; status?: string }) => {
      const q = params ? '?' + new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
      ) : ''
      return request<Episode[]>(`/episodes${q}`)
    },
    get:         (id: string) => request<Episode & { stages: Stage[]; recent_reports: DailyReport[] }>(`/episodes/${id}`),
    create:      (body: Partial<Episode>) => request('/episodes', { method: 'POST', body: JSON.stringify(body) }),
    update:      (id: string, body: Partial<Episode>) => request(`/episodes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    updateStage: (
      id: string,
      stage: string,
      status: Stage['status'],
      progress?: {
        progress_pages?: number
        target_pages?: number | null
        progress_note?: string | null
      },
    ) =>
      request<{ success: boolean; episode_status: string | null; plan_sync?: { updated: number } }>(
        `/episodes/${id}/stages/${stage}`,
        { method: 'PATCH', body: JSON.stringify({ status, ...progress }) },
      ),
    delete: (id: string) => request(`/episodes/${id}`, { method: 'DELETE' }),
  },

  // アシスタント
  assistants: {
    list: (params?: { status?: 'active' | 'leaving' | 'retired' | 'all' }) =>
      request<Assistant[]>(`/assistants${params?.status ? `?status=${params.status}` : ''}`),
    create: (body: Partial<Assistant>) => request('/assistants', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<Assistant> & { stage_capabilities?: AssistantStageCapability[] }) =>
      request(`/assistants/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    retire: (id: string, body: Record<string, unknown>) =>
      request(`/assistants/${id}/retirement`, { method: 'PATCH', body: JSON.stringify(body) }),
    reactivate: (id: string) =>
      request(`/assistants/${id}/reactivate`, { method: 'PATCH', body: JSON.stringify({}) }),
    onboarding: (body: {
      name: string
      email: string
      display_name?: string
      phone?: string
      contact_note?: string
      notes?: string
      role_type?: string
      joined_at?: string
      daily_rate?: number
      training_enabled?: number
      training_daily_rate?: number
      training_days_completed?: number
      training_days_required?: number
      transport_cap?: number
      overtime_hourly?: number
      payroll_enabled?: number
      shift_enabled?: number
      report_enabled?: number
      login_enabled?: number
      initial_password?: string
      must_change_password?: number
      onboarding_note?: string
      onboarding_status?: string
    }) =>
      request<{ success: boolean; assistant_id: string; name: string; email: string; login_created: boolean }>(
        '/assistants/onboarding', { method: 'POST', body: JSON.stringify(body) }
      ),
  },

  // ヘルプスタッフ（管理者専用）
  helpStaff: {
    list: (params?: { status?: 'active' | 'all' }) =>
      request<HelpStaff[]>(`/help-staff${params?.status ? `?status=${params.status}` : ''}`),
    create: (body: {
      name: string
      default_unit_rate?: number
      default_transport_fee?: number
      default_overtime_rate?: number
      notes?: string
    }) => request<{ success: boolean; id: string }>('/help-staff', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<HelpStaff>) =>
      request<{ success: boolean }>(`/help-staff/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  },

  // ヘルプ勤務エントリ（管理者専用）
  helpWorkEntries: {
    list: (params?: { month?: string; help_staff_id?: string; status?: string }) => {
      const q = params ? '?' + new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
      ) : ''
      return request<HelpWorkEntry[]>(`/help-work-entries${q}`)
    },
    create: (body: Pick<HelpWorkEntryWriteFields, 'help_staff_id' | 'work_date' | 'payment_target_month'> & Partial<HelpWorkEntryWriteFields>) =>
      request<{ success: boolean; id: string }>('/help-work-entries', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<HelpWorkEntryWriteFields>) =>
      request<{ success: boolean }>(`/help-work-entries/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    cancel: (id: string, body?: { reason?: string; confirm_paid?: boolean }) =>
      request<{ success: boolean }>(`/help-work-entries/${id}/cancel`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
    restore: (id: string, body?: { reset_content?: boolean }) =>
      request<{ success: boolean }>(`/help-work-entries/${id}/restore`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
    reset: (id: string, body?: { confirm_paid?: boolean }) =>
      request<{ success: boolean }>(`/help-work-entries/${id}/reset`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
    confirm: (id: string) => request<{ success: boolean }>(`/help-work-entries/${id}/confirm`, { method: 'POST' }),
    markPaid: (id: string) => request<{ success: boolean }>(`/help-work-entries/${id}/paid`,   { method: 'POST' }),
    issueToken: (id: string, expiresInDays?: number) =>
      request<{ success: boolean; raw_token: string; expires_at: string }>(
        `/help-work-entries/${id}/token`, { method: 'POST', body: JSON.stringify({ expires_in_days: expiresInDays }) }
      ),
    revokeToken: (id: string) => request<{ success: boolean }>(`/help-work-entries/${id}/revoke-token`, { method: 'POST' }),
    annualSummary: (year: number, statusFilter?: 'paid' | 'confirmed') =>
      request<HelpAnnualSummary>(
        `/help-work-entries/annual-summary?year=${year}${statusFilter ? `&status_filter=${statusFilter}` : ''}`
      ),
  },

  // 統合月次一覧（通常アシスタント＋ヘルプスタッフ、管理者専用・読み取り専用）
  monthlyPayees: {
    list: (month: string) => request<{ month: string; payees: MonthlyPayee[] }>(`/monthly-payees?month=${month}`),
  },

  // ヘルプスタッフ本人向け公開提出画面（未ログイン・トークン1件スコープ）
  helpSubmit: {
    get: (token: string) => helpSubmitRequest<HelpSubmitData>(token),
    // reported_overtime_minutes は送信しない（サーバー側が start_time/end_time/break_minutes から必ず再計算する）
    submit: (token: string, body: HelpSubmitUpdateBody) =>
      helpSubmitRequest<{ success: boolean }>(token, { method: 'PUT', body: JSON.stringify(body) }),
    saveDraft: (token: string, body: HelpSubmitUpdateBody) =>
      helpSubmitRequest<{ success: boolean }>(token, { method: 'PUT', body: JSON.stringify(body) }, '/draft'),
    uploadImage: (token: string, file: File) => helpSubmitUploadImage(token, file),
    deleteImage: (token: string, key: string) =>
      helpSubmitRequest<{ success: boolean }>(token, { method: 'DELETE', body: JSON.stringify({ key }) }, '/images'),
  },

  // 勤怠
  attendance: {
    list:    (month?: string) => request<Attendance[]>(`/attendance${month ? `?month=${month}` : ''}`),
    summary: (month?: string) =>
      request<{ month: string; assistants: any[]; grand_total: number }>(
        `/attendance/salary-summary${month ? `?month=${month}` : ''}`
      ),
    record:      (body: Partial<Attendance>) => request('/attendance', { method: 'POST', body: JSON.stringify(body) }),
    quickRecord: (assistant_id: string, work_date: string, transport_fee?: number) =>
      request('/attendance/quick-record', { method: 'POST', body: JSON.stringify({ assistant_id, work_date, transport_fee }) }),
    delete: (id: string) => request(`/attendance/${id}`, { method: 'DELETE' }),
  },

  // 日報
  reports: {
    deleteOwn: (id: string) =>
      request<{ success: boolean; report_date: string }>(`/my/daily-reports/${id}`, { method: 'DELETE' }),
    list: (params?: { date?: string; assistant_id?: string; start_date?: string; end_date?: string; handover_only?: string; scope?: 'mine' | 'target' | 'all' }) => {
      const q = params ? '?' + new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
      ) : ''
      return request<DailyReport[]>(`/reports${q}`)
    },
    create: (body: Partial<DailyReport>) => request('/reports', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<DailyReport>) =>
      request<{ success: boolean; id: string }>(`/reports/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    delete: (id: string) => request(`/reports/${id}`, { method: 'DELETE' }),
  },

  // 支出
  expenses: {
    list: (params?: { month?: string; is_paid?: string; is_fixed?: string }) => {
      const q = params ? '?' + new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
      ) : ''
      return request<Expense[]>(`/expenses${q}`)
    },
    summary:  (month: string) =>
      request<{ total: number; paid: number; unpaid: number; unpaid_count: number }>(`/expenses/summary/${month}`),
    create:   (body: Partial<Expense>) =>
      request('/expenses', { method: 'POST', body: JSON.stringify(body) }),
    markPaid: (id: string, paid_at?: string) =>
      request(`/expenses/${id}/mark-paid`, { method: 'PATCH', body: JSON.stringify({ paid_at }) }),
    markUnpaid: (id: string) =>
      request(`/expenses/${id}/mark-unpaid`, { method: 'PATCH', body: '{}' }),
    delete:   (id: string) => request(`/expenses/${id}`, { method: 'DELETE' }),
    update:   (id: string, body: Partial<Pick<Expense,
      'category' | 'description' | 'amount' | 'payment_method' | 'due_at' | 'notes' |
      'payment_account_key' | 'cashflow_scope'
    >>) =>
      request(`/expenses/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },

  // 収入
  revenues: {
    list: (params?: { month?: string; work_id?: string }) => {
      const q = params ? '?' + new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
      ) : ''
      return request<Revenue[]>(`/revenues${q}`)
    },
    summary:      (month: string) => request(`/revenues/summary/${month}`),
    create:       (body: Partial<Revenue>) => request('/revenues', { method: 'POST', body: JSON.stringify(body) }),
    markReceived: (id: string, received_at?: string) =>
      request(`/revenues/${id}/mark-received`, { method: 'PATCH', body: JSON.stringify({ received_at }) }),
    delete: (id: string) => request(`/revenues/${id}`, { method: 'DELETE' }),
    update: (id: string, body: Partial<Pick<Revenue, 'revenue_type' | 'amount' | 'tax_reserve_rate' | 'expected_at' | 'received_at' | 'description'>>) =>
      request(`/revenues/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },

  // イベント・TODO
  events: {
    list:   (month: string) => request<any[]>(`/events?month=${month}`),
    create: (body: { event_date: string; title: string; event_type?: string; memo?: string }) =>
      request('/events', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<{ title: string; event_type: string; memo: string | null; is_done: number }>) =>
      request(`/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => request(`/events/${id}`, { method: 'DELETE' }),
  },

  autoEvents: {
    list: (month: string) => request<any[]>(`/events/auto?month=${month}`),
  },

  // 毎月固定の予定（ダッシュボードのカレンダー。管理者のみ）
  fixedSchedule: {
    list: () => request<{ month: string; items: { id: string; label: string; event_type: string; day_of_month: number | null }[] }>('/events/fixed-schedule'),
    save: (items: { id?: string; label: string; day_of_month: number }[]) =>
      request<{ success: boolean }>('/events/fixed-schedule', { method: 'PUT', body: JSON.stringify({ items }) }),
  },

  // シフト希望
  shiftRequests: {
    list:     (month: string) => request<any>(`/shift-requests?month=${month}`),
    missing:  (month: string) => request<any>(`/shift-requests/missing?month=${month}`),
    markSeen: (id: string) =>
      request<{ success: boolean }>(`/shift-requests/${id}/seen`, { method: 'PATCH', body: '{}' }),
  },

  // 確定シフト
  confirmedShifts: {
    list: (month: string) => request<any>(`/confirmed-shifts?month=${month}`),
    save: (assistant_id: string, target_month: string, confirmed_dates: string[]) =>
      request('/confirmed-shifts', { method: 'POST', body: JSON.stringify({ assistant_id, target_month, confirmed_dates }) }),
    saveAll: (target_month: string, shifts: { assistant_id: string; confirmed_dates: string[] }[]) =>
      request('/confirmed-shifts/bulk', { method: 'POST', body: JSON.stringify({ target_month, shifts }) }),
  },

  // シフト締切
  shiftDeadlines: {
    get: (month: string) => request<any>(`/shift-deadlines?month=${month}`),
    set: (target_month: string, deadline_date: string, note?: string) =>
      request('/shift-deadlines', { method: 'POST', body: JSON.stringify({ target_month, deadline_date, note }) }),
  },

  // シフト通知
  shiftNotifications: {
    send:   (month: string, message?: string) =>
      request<any>('/shift-notifications/send', { method: 'POST', body: JSON.stringify({ month, message }) }),
    status: (month: string) => request<any>(`/shift-notifications/status?month=${month}`),
  },

  // 月次支払
  monthlyPayments: {
    list: (month: string) => request<any>(`/monthly-payments?month=${month}`),
    generate: (month: string, options?: { use_confirmed_claims?: boolean }) =>
      request<{
        success: boolean; count: number; month: string
        updated: number; skipped: number; skipped_names: string[]
        claims_applied: number; use_confirmed_claims: boolean
      }>(
        '/monthly-payments/generate', { method: 'POST', body: JSON.stringify({ month, ...options }) }
      ),
    updateStatus:    (id: string, status: string, paid_at?: string) =>
      request(`/monthly-payments/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status, paid_at }) }),
    revertPaid:      (id: string) =>
      request(`/monthly-payments/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'approved' }) }),
    revertReviewed:  (id: string) =>
      request(`/monthly-payments/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status: 'draft' }) }),
    bulkReview:  (month: string) =>
      request('/monthly-payments/bulk-review',  { method: 'PATCH', body: JSON.stringify({ month }) }),
    bulkConfirm: (month: string) =>
      request('/monthly-payments/bulk-confirm', { method: 'PATCH', body: JSON.stringify({ month }) }),
    bulkPaid:    (month: string, paid_at?: string) =>
      request('/monthly-payments/bulk-paid',    { method: 'PATCH', body: JSON.stringify({ month, paid_at }) }),
    csvUrl: (month: string) =>
      `${import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'}/monthly-payments/csv?month=${month}&format=simple`,
  },

  // 画像アップロード
  upload: {
    reportImage: async (file: File): Promise<{ url: string; key: string }> => {
      const BASE = import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`${BASE}/upload/report-image`, { method: 'POST', body: form, credentials: 'include' })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error((err as any).error ?? 'Upload failed')
      }
      return res.json()
    },
    fileUrl: (key: string) => {
      const BASE = import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'
      return `${BASE}/upload/file/${key}`
    },
  },

  // やよい会計エクスポート
  export: {
    yayoiUrl:          (month: string, split: boolean) =>
      `${import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'}/export/yayoi?month=${month}&split=${split ? 1 : 0}`,
    paymentDetailUrl:  (month: string) =>
      `${import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'}/export/payment-detail?month=${month}`,
    expenseSummaryUrl: (month: string) =>
      `${import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'}/export/expense-summary?month=${month}`,
  },

  // 月締め・承認フロー
  monthlyCloses: {
    get:     (month: string) => request<any>(`/monthly-closes?month=${month}`),
    review:  (month: string) => request('/monthly-closes/review',  { method: 'POST', body: JSON.stringify({ month }) }),
    approve: (month: string) => request('/monthly-closes/approve', { method: 'POST', body: JSON.stringify({ month }) }),
    reopen:  (month: string, note: string) => request('/monthly-closes/reopen', { method: 'POST', body: JSON.stringify({ month, note }) }),
  },

  // 月次レポート
  monthlyReport: {
    get:    (month: string) => request<any>(`/monthly-report?month=${month}`),
    csvUrl: (month: string) =>
      `${import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'}/monthly-report/csv?month=${month}`,
  },

  // 確定シフト通知
  shiftConfirmationNotifications: {
    preview: (month: string) =>
      request<{
        month: string; total_confirmed: number; can_send: number; no_email: number
        targets: {
          assistant_id: string; name: string; email: string; days_count: number
          changed: boolean; is_test_email: boolean
        }[]
        no_email_names: string[]; last_sent_at: string | null
      }>(`/shift-confirmation-notifications/preview?month=${month}`),
    send: (month: string, assistantIds: string[], message?: string) =>
      request<{
        success: boolean; sent: number; skipped: number
        sent_names: string[]; skipped_names: string[]
        results: { assistant_id: string; name: string; success: boolean; reason?: string }[]
      }>(
        '/shift-confirmation-notifications/send',
        { method: 'POST', body: JSON.stringify({ month, message, assistant_ids: assistantIds }) }
      ),
  },

  // アシスタント自己参照
  my: {
    closes: {
      get: (month: string) => request<{ month: string; closed: boolean }>(`/my/closes?month=${month}`),
    },
    shifts: {
      get: (month: string) =>
        request<{
          month: string; assistant_id: string; assistant_name?: string
          confirmed_dates: string[]; note: string | null; days_count: number
          daily_rate?: number; estimated_pay?: number
        }>(`/my/shifts?month=${month}`),
    },
    attendance: {
      list: (month: string) => request<Attendance[]>(`/my/attendance?month=${month}`),
      record: (body: {
        work_date: string; check_in?: string; check_out?: string
        overtime_min?: number; transport_fee?: number; meal_provided?: number
        work_type?: 'full_time' | 'part_time'; work_minutes?: number; break_minutes?: number; notes?: string
      }) =>
        request<{ success: boolean; daily_total: number; id: string | null }>(
          '/my/attendance', { method: 'POST', body: JSON.stringify(body) }
        ),
      delete: (id: string) =>
        request<{ success: boolean; deleted?: Attendance }>(`/my/attendance/${id}`, { method: 'DELETE' }),
    },
    payrollClaims: {
      get: (month: string) => request<any>(`/my/payroll-claims?month=${month}`),
      save: (body: any) =>
        request<{ success: boolean; status: string; claim: any }>(
          '/my/payroll-claims', { method: 'POST', body: JSON.stringify(body) }
        ),
      // その他経費の領収書画像
      uploadReceipt: async (targetMonth: string, file: File): Promise<{ url: string; key: string; receipt_urls: string[] }> => {
        const BASE = import.meta.env.VITE_API_BASE ?? 'https://mangaos.studio-nekonomiya.workers.dev/api'
        const form = new FormData()
        form.append('file', file)
        form.append('target_month', targetMonth)
        const res = await fetch(`${BASE}/my/payroll-claims/receipts`, { method: 'POST', body: form, credentials: 'include' })
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: res.statusText }))
          throw new Error((err as any).error ?? 'Upload failed')
        }
        return res.json()
      },
      deleteReceipt: (targetMonth: string, url: string) =>
        request<{ success: boolean; receipt_urls: string[] }>(
          '/my/payroll-claims/receipts',
          { method: 'DELETE', body: JSON.stringify({ target_month: targetMonth, url }) }
        ),
    },
    shiftRequests: {
      get: (month: string) => request<any>(`/my/shift-requests?month=${month}`),
      submit: (body: { target_month: string; available_dates: string[]; note?: string }) =>
        request<{ success: boolean; updated: boolean; request: any }>(
          '/my/shift-requests', { method: 'POST', body: JSON.stringify(body) }
        ),
    },
    // プロフィール（デフォルト交通費・研修状況）
    profile: {
      get: () => request<{
        id: string; name: string; display_name: string | null; email: string | null
        default_transportation_fee: number
        training_enabled: number; training_days_completed: number
        training_days_required: number; training_daily_rate: number; daily_rate: number
      }>('/my/profile'),
      update: (body: { default_transportation_fee?: number }) =>
        request<{ success: boolean; default_transportation_fee: number }>(
          '/my/profile', { method: 'PATCH', body: JSON.stringify(body) }
        ),
    },
    // 給与明細（支払済みのみ・本人閲覧）
    payslips: {
      list: () =>
        request<{ id: string; payroll_month: string; pay_date: string | null; net_pay: number; status: string }[]>(
          '/my/payroll'
        ),
      get: (id: string) => request<any>(`/my/payroll/${id}`),
      logIssue: (id: string) =>
        request<{ success: boolean }>(`/my/payroll/${id}/log-issue`, { method: 'POST' }),
    },
    // 賞与明細（支払済みのみ・本人閲覧）
    bonuses: {
      list: () =>
        request<{ id: string; bonus_name: string; payment_date: string; net_amount: number; status: string }[]>(
          '/my/bonuses'
        ),
      get: (id: string) => request<any>(`/my/bonuses/${id}`),
      logIssue: (id: string) =>
        request<{ success: boolean }>(`/my/bonuses/${id}/log-issue`, { method: 'POST' }),
    },
    // 給与申告修正依頼
    payrollCorrectionRequests: {
      list: (month: string) =>
        request<{ month: string; requests: any[] }>(`/my/payroll-correction-requests?month=${month}`),
      submit: (body: {
        target_month:      string
        reason:            string
        /** 元の申告内容（FormState形式） */
        original_payload:  Record<string, any>
        /** 修正後の全体（FormState形式） */
        requested_payload: Record<string, any>
        /** 変更されたフィールドの差分 */
        diff_payload?:     Array<{ label: string; before: string; after: string }>
      }) =>
        request<{ success: boolean; id: number }>(
          '/my/payroll-correction-requests', { method: 'POST', body: JSON.stringify(body) }
        ),
    },
  },

  // 管理者: 給与申告確認
  adminPayrollClaims: {
    list:   (month: string) => request<any>(`/payroll-claims?month=${month}`),
    update: (id: string, body: {
      status?: string
      admin_notes?: string | null
      confirmed_amount?: number | null
      confirmed_base_pay?: number | null
      confirmed_overtime_pay?: number | null
      confirmed_transport_total?: number | null
      confirmed_other_expense_total?: number | null
      confirmed_other_allowance_total?: number | null
    }) =>
      request<{ success: boolean; claim: any }>(`/payroll-claims/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    sendEmail: (id: string, payload?: { subject?: string; body?: string }) =>
      request<{ success: boolean; to: string; subject: string; sent_at: string | null }>(
        `/payroll-claims/${id}/send-email`,
        { method: 'POST', body: JSON.stringify(payload ?? {}) }
      ),
    emailLogs: (month: string) =>
      request<{ logs: any[] }>(`/payroll-claims/email-logs?month=${month}`),
    // 給与申告未提出者リマインド
    reminderTargets: (month: string) =>
      request<{
        month: string; closed: boolean
        total_missing: number; can_send: number
        no_email_names: string[]; test_email_names: string[]
        targets: {
          assistant_id: string; name: string; email: string | null
          can_send: boolean; is_test_email: boolean
        }[]
      }>(`/payroll-claims/reminder-targets?month=${month}`),
    sendReminder: (month: string, assistantIds?: string[], message?: string) =>
      request<{
        success: boolean; sent: number; skipped: number
        sent_names: string[]; skipped_names: string[]
        results: { assistant_id: string; name: string; success: boolean; reason?: string }[]
        message?: string
      }>('/payroll-claims/remind', {
        method: 'POST',
        body: JSON.stringify({ month, assistant_ids: assistantIds, message }),
      }),
    // 修正依頼
    corrections: {
      list:  (month?: string, status?: string) => {
        const q = new URLSearchParams()
        if (month)  q.set('month',  month)
        if (status) q.set('status', status)
        return request<{ corrections: any[]; pending_count: number }>(`/payroll-claims/corrections?${q}`)
      },
      count: () => request<{ pending_count: number }>('/payroll-claims/corrections/count'),
      update: (id: number, body: { action: 'approve' | 'reject' | 'resolve'; admin_note?: string }) =>
        request<{ success: boolean; status: string }>(
          `/payroll-claims/corrections/${id}`, { method: 'PATCH', body: JSON.stringify(body) }
        ),
    },
  },

  // 固定費テンプレート管理
  fixedExpenseTemplates: {
    list: (includeInactive = false) =>
      request<any[]>(`/fixed-expense-templates${includeInactive ? '?include_inactive=1' : ''}`),
    create: (body: {
      category: string; description: string; amount: number
      payment_day?: number; payment_method?: string; notes?: string | null
      payment_account_key?: string; active_months?: string; recurrence_type?: 'annual' | 'specific_months'
    }) =>
      request<{ success: boolean; template: any }>(
        '/fixed-expense-templates', { method: 'POST', body: JSON.stringify(body) }
      ),
    update: (id: string, body: Partial<{
      category: string; description: string; amount: number
      payment_day: number; payment_method: string; notes: string | null
      payment_account_key: string; active_months: string; recurrence_type: 'annual' | 'specific_months'
    }>) =>
      request<{ success: boolean; template: any }>(
        `/fixed-expense-templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }
      ),
    toggleActive: (id: string) =>
      request<{ success: boolean; id: string; is_active: number }>(
        `/fixed-expense-templates/${id}/toggle-active`, { method: 'POST', body: '{}' }
      ),
    delete: (id: string) =>
      request<{ success: boolean }>(`/fixed-expense-templates/${id}`, { method: 'DELETE' }),
    setOccurrences: (id: string, occurrences: { target_month: string; amount: number; payment_day: number }[]) =>
      request<{ success: boolean; occurrences: any[] }>(
        `/fixed-expense-templates/${id}/occurrences`, { method: 'PUT', body: JSON.stringify({ occurrences }) }
      ),
    generateMonth: (targetMonth: string) =>
      request<{
        success: boolean; target_month: string; created: number; skipped: number
        items: { description: string; amount: number; due_at: string; status: 'created' | 'skipped' }[]
      }>('/fixed-expense-templates/generate-month', { method: 'POST', body: JSON.stringify({ targetMonth }) }),
  },

  // ★ 追加: 口座残高管理
  bankAccounts: {
    list: () => request<BankAccountSummary[]>('/bank-accounts'),
    update: (accountKey: string, payload: {
      name?: string; purpose?: string; current_balance?: number; alert_enabled?: number
    }) =>
      request<{ success: boolean; account: BankAccountSummary }>
        (`/bank-accounts/${accountKey}`, { method: 'PATCH', body: JSON.stringify(payload) }),
    adjust: (accountKey: string, payload: { adjustment_amount: number; reason?: string }) =>
      request<{ success: boolean; balance_after: number }>(
        `/bank-accounts/${accountKey}/adjust`, { method: 'POST', body: JSON.stringify(payload) }
      ),
    withdrawalCheck: () => request<WithdrawalCheckResult>('/bank-accounts/withdrawal-check'),
  },

  // 給与明細管理
  payroll: {
    list: (month?: string, year?: number, assistantId?: string) => {
      const q = new URLSearchParams()
      if (month)       q.set('month',        month)
      if (year)        q.set('year',         String(year))
      if (assistantId) q.set('assistant_id', assistantId)
      return request<any[]>(`/payroll?${q}`)
    },
    get:    (id: string) => request<any>(`/payroll/${id}`),
    create: (body: Record<string, any>) =>
      request<{ success: boolean; record: any }>('/payroll', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Record<string, any>) =>
      request<{ success: boolean; record: any }>(`/payroll/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    updateStatus: (id: string, status: string, reason?: string) =>
      request<{ success: boolean; record: any }>(`/payroll/${id}/status`, {
        method: 'PATCH', body: JSON.stringify({ status, reason }),
      }),
    cancel: (id: string) => request<{ success: boolean }>(`/payroll/${id}`, { method: 'DELETE' }),
    logIssue: (id: string, issueType = 'pdf') =>
      request<{ success: boolean }>(`/payroll/${id}/log-issue`, { method: 'POST', body: JSON.stringify({ issue_type: issueType }) }),
    importFromMonthlyPayment: (month: string) =>
      request<{
        success: boolean; total: number
        inserted: number; updated: number
        skippedConfirmed: number; skippedPaid: number; skippedOther: number
        imported: number; skipped: number
      }>(
        '/payroll/import', { method: 'POST', body: JSON.stringify({ month }) }
      ),
  },

  // 賞与管理
  bonuses: {
    list: (params?: { assistantId?: string; year?: number; status?: string }) => {
      const q = new URLSearchParams()
      if (params?.assistantId) q.set('assistant_id', params.assistantId)
      if (params?.year)        q.set('year',         String(params.year))
      if (params?.status)      q.set('status',       params.status)
      return request<any[]>(`/bonuses?${q}`)
    },
    get:    (id: string) => request<any>(`/bonuses/${id}`),
    create: (body: Record<string, any>) =>
      request<{ success: boolean; record: any }>('/bonuses', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Record<string, any>) =>
      request<{ success: boolean; record: any }>(`/bonuses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    cancel: (id: string) => request<{ success: boolean }>(`/bonuses/${id}`, { method: 'DELETE' }),
    logIssue: (id: string, issueType = 'pdf') =>
      request<{ success: boolean }>(`/bonuses/${id}/log-issue`, { method: 'POST', body: JSON.stringify({ issue_type: issueType }) }),
  },

  // 税務設定
  taxProfiles: {
    list: (year?: number, assistantId?: string) => {
      const q = new URLSearchParams()
      if (year)        q.set('year',         String(year))
      if (assistantId) q.set('assistant_id', assistantId)
      return request<any[]>(`/tax-profiles?${q}`)
    },
    upsert: (body: Record<string, any>) =>
      request<{ success: boolean; profile: any }>('/tax-profiles', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Record<string, any>) =>
      request<{ success: boolean }>(`/tax-profiles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  },

  // 制作進行計画
  productionPlanning: {
    getSettings: () =>
      request<{ capacity_settings: CapacitySetting[]; single_task_rules: SingleTaskRule[] }>(
        '/production-planning/settings',
      ),
    updateSettings: (payload: {
      capacity_settings?: {
        id: string
        normal_capacity: number
        rush_capacity: number
        can_work_weekends: number
        can_parallel: number
        is_active: number
      }[]
      single_task_rules?: {
        id: string
        label: string
        default_days: number
        has_page_quota: number
        note: string | null
        is_active: number
      }[]
    }) =>
      request<{ success: boolean }>(
        '/production-planning/settings',
        { method: 'PUT', body: JSON.stringify(payload) },
      ),
    getResourceSettings: () =>
      request<ResourceSettings>('/production-planning/resource-settings'),
    updateResourceSettings: (payload: {
      windows_capacity: number
      mac_capacity: number
      is_enabled: number
      notes: string | null
    }) =>
      request<{ success: boolean; resource_settings: ResourceSettings }>(
        '/production-planning/resource-settings',
        { method: 'PUT', body: JSON.stringify(payload) },
      ),
    getTodayPlan: (date?: string) =>
      request<TodayPlan>(
        `/production-planning/today${date ? `?date=${date}` : ''}`,
      ),
    // Phase 4-A
    planPreview: (params?: { start_date?: string; days?: number }) => {
      const q = params
        ? '?' + new URLSearchParams(
            Object.fromEntries(
              Object.entries(params)
                .filter(([, v]) => v !== undefined)
                .map(([k, v]) => [k, String(v)])
            )
          )
        : ''
      return request<{
        start_date: string
        end_date: string
        days: ProductionPlanDay[]
        day_summaries: ProductionPlanDaySummary[]  // Phase 5-A
      }>(
        `/production-planning/plan-preview${q}`,
      )
    },
    generatePlan: (payload: {
      start_date?: string
      days?: number
      overwrite_unlocked?: boolean
      note?: string
    }) =>
      request<{ success: boolean; plan_run_id: string; created_count: number; kept_locked_count: number }>(
        '/production-planning/plans/generate',
        { method: 'POST', body: JSON.stringify(payload) },
      ),
    listPlans: (params?: { start_date?: string; end_date?: string }) => {
      const q = params
        ? '?' + new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
          )
        : ''
      return request<{ items: ProductionPlanItem[]; day_summaries: ProductionPlanDaySummary[] }>(
        `/production-planning/plans${q}`
      )
    },
    updatePlanItem: (id: string, payload: Partial<{
      plan_date:      string
      planned_amount: number
      is_locked:      number
      status:         string
      manual_note:    string | null
      conflict_level: string | null
      conflict_note:  string | null
    }>) =>
      request<{ success: boolean; item: ProductionPlanItem }>(
        `/production-planning/plans/${id}`,
        { method: 'PATCH', body: JSON.stringify(payload) },
      ),
    lockPlanItem: (id: string) =>
      request<{ success: boolean }>(`/production-planning/plans/${id}/lock`, { method: 'POST', body: '{}' }),
    unlockPlanItem: (id: string) =>
      request<{ success: boolean }>(`/production-planning/plans/${id}/unlock`, { method: 'POST', body: '{}' }),
    skipPlanItem: (id: string) =>
      request<{ success: boolean }>(`/production-planning/plans/${id}/skip`, { method: 'POST', body: '{}' }),
    // Phase 4-C: 延期（翌稼働日へ移動）
    deferPlanItem: (id: string) =>
      request<{ success: boolean; from_date: string; to_date: string; item: ProductionPlanItem }>(
        `/production-planning/plans/${id}/defer`,
        { method: 'POST', body: '{}' },
      ),
    // Phase 4-C: 指定日へ移動
    movePlanItem: (id: string, payload: { plan_date: string; reason?: string }) =>
      request<{ success: boolean; from_date: string; to_date: string; item: ProductionPlanItem }>(
        `/production-planning/plans/${id}/move`,
        { method: 'PATCH', body: JSON.stringify(payload) },
      ),
    // Phase 4-B: 保存済み制作予定取得
    todayPlan: (params?: { date?: string }) => {
      const q = params?.date ? `?date=${params.date}` : ''
      return request<ProductionPlanToday>(`/production-planning/plans/today${q}`)
    },
  },

  // 日報→工程進捗 反映
  reportProgressReflections: {
    candidates: (params?: {
      start_date?:         string
      end_date?:           string
      only_pending?:       string
      daily_report_id?:    string
      help_work_entry_id?: string
    }) => {
      const q = params
        ? '?' + new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
          )
        : ''
      return request<{ candidates: ReportProgressCandidate[] }>(
        `/report-progress-reflections/candidates${q}`
      )
    },
    apply: (payload: {
      source_type?:        'assistant' | 'help'
      daily_report_id?:    string
      help_work_entry_id?: string
      help_staff_id?:      string
      detail_index:        number
      episode_id:          string
      stage_key:           string
      pages_delta:         number
      work_id?:             string
      note?:                string
    }) =>
      request<{
        success:         boolean
        progress_before: number
        progress_after:  number
        target_pages:    number | null
        reconciled?:     boolean
        message?:        string
      }>(
        '/report-progress-reflections/apply',
        { method: 'POST', body: JSON.stringify(payload) }
      ),
    applyBulk: (payload: {
      items: {
        source_type?:        'assistant' | 'help'
        daily_report_id?:    string
        help_work_entry_id?: string
        help_staff_id?:      string
        detail_index:        number
        episode_id:          string
        stage_key:           string
        pages_delta:         number
        work_id?:             string
        note?:                string
      }[]
    }) =>
      request<{
        results:    unknown[]
        succeeded:  number
        failed:     number
        reconciled: number
        total:      number
      }>(
        '/report-progress-reflections/apply-bulk',
        { method: 'POST', body: JSON.stringify(payload) }
      ),
    history: (params?: { start_date?: string; end_date?: string }) => {
      const q = params
        ? '?' + new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
          )
        : ''
      return request<{ history: ReportProgressReflectionHistory[] }>(
        `/report-progress-reflections/history${q}`
      )
    },
    undo: (id: string, payload?: { reason?: string }) =>
      request<{ success: boolean; progress_before: number; progress_after: number; restored_to: number }>(
        `/report-progress-reflections/${id}/undo`,
        { method: 'POST', body: JSON.stringify(payload ?? {}) }
      ),
  },

  // 源泉所得税集計
  withholding: {
    summary: (year: number) => request<any>(`/withholding-summary?year=${year}`),
    assistants: (year: number) => request<any>(`/withholding-summary/assistants?year=${year}`),
  },
}